// Derived PrepCook checklist stage (DECISIONS §E.7, README §6.7).
//
// Shown when a file declares no timed prep phase but carries prerequisites
// mise (see derivePrep.ts). Unlike the timed PrepPane this is a calm,
// sequential checklist: the prerequisites entries in order, each a single
// confirmation checkbox with its resolved resource / tools, under ONE
// advisory budget timer (no per-task clocks, no overrun panel — nothing is
// cooking yet). When every item is confirmed it transitions to LiveCook,
// exactly like the timed prep stage.
//
// The budget timer is advisory: a declared `prepTime` seeds it; otherwise a
// default is shown and labelled approximate. It is never a hard deadline.

import { useMemo, useState } from 'react';
import type { AssistEntry } from '@cookchow/shared';
import {
  AssistPopover,
  AssistText,
  ChefHat,
  Checkbox,
  ColourToggle,
  HandednessToggle,
  NotePopover,
  SpeakerToggle,
  Timeline,
  TimerDisplay,
} from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { useElapsedSeconds } from './useElapsedSeconds';
import { useAssist } from './useAssist';
import { correctedNow, formatHHMM } from './helpers';
import { PhaseEndPanel } from './PhaseEndPanel';
import { buildPrepChecklist, type PrepStep } from './derivePrep';

interface PrepChecklistPaneProps {
  onTransition: (prepElapsedSeconds: number) => void;
}

function StepMeta({ step }: { step: PrepStep }) {
  const parts: string[] = [];
  for (const r of step.resources) parts.push(r.measure ? `${r.measure} ${r.text}` : r.text);
  const tools = [...step.utensils, ...step.sundries];
  if (tools.length > 0) parts.push(`— ${tools.join(', ')}`);
  if (parts.length === 0) return null;
  return <div style={{ fontSize: '0.85rem', color: theme.text.muted }}>{parts.join('  ·  ')}</div>;
}

export function PrepChecklistPane({ onTransition }: PrepChecklistPaneProps) {
  const recipe = useAppStore((s) => s.recipe);
  const recipeFile = useAppStore((s) => s.recipeFile);
  const prepStartedAt = useAppStore((s) => s.prepStartedAt);
  const budgetSeconds = useAppStore((s) => s.prepBudgetSeconds);
  const budgetApproximate = useAppStore((s) => s.prepBudgetApproximate);
  const confirmedPrepIds = useAppStore((s) => s.confirmedPrepIds);
  const confirmPrepItem = useAppStore((s) => s.confirmPrepItem);
  const handedness = useAppStore((s) => s.handedness);
  const setHandedness = useAppStore((s) => s.setHandedness);
  const soundEnabled = useAppStore((s) => s.soundEnabled);
  const setSoundEnabled = useAppStore((s) => s.setSoundEnabled);
  const colorScheme = useAppStore((s) => s.colorScheme);
  const setColorScheme = useAppStore((s) => s.setColorScheme);
  const clockOffset = useAppStore((s) => s.clockOffset);

  const elapsed = useElapsedSeconds(prepStartedAt);
  const steps = useMemo(() => (recipe ? buildPrepChecklist(recipe) : []), [recipe]);
  const confirmed = useMemo(() => new Set(confirmedPrepIds), [confirmedPrepIds]);

  // Chef-hat glossary popover and Kitchen Assist help popover — one open at
  // a time across both, so opening either closes the other.
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const { matcher: assistMatcher, resolveEntry: resolveAssistEntry } = useAssist();
  const assistEnabled = !!assistMatcher;
  const [assist, setAssist] = useState<{
    stepId: string;
    word: string;
    entry: AssistEntry | null;
  } | null>(null);

  const openNote = (id: string): void => {
    setAssist(null);
    setOpenNoteId((cur) => (cur === id ? null : id));
  };

  const handleAssistWord = (stepId: string, word: string): void => {
    setOpenNoteId(null);
    setAssist({ stepId, word, entry: null });
    void resolveAssistEntry(word)
      .then((entry) =>
        setAssist((cur) =>
          cur && cur.stepId === stepId && cur.word === word ? { ...cur, entry } : cur,
        ),
      )
      .catch(() =>
        setAssist((cur) => (cur && cur.stepId === stepId && cur.word === word ? null : cur)),
      );
  };

  // The derived checklist runs against its advisory budget (declared
  // prepTime, else the 30-minute default). The Complete panel appears when
  // the budget elapses OR every item is confirmed (an early finish); on an
  // early finish it shows just "PrepCook Complete" (no "Time's Up!"). Ticking
  // Complete (enabled once every item is confirmed) advances to LiveCook
  // (DECISIONS §F.3).
  const timesUp = budgetSeconds != null && budgetSeconds > 0 && elapsed >= budgetSeconds;
  const allConfirmed = steps.length > 0 && steps.every((s) => confirmed.has(s.id));
  const showEndPanel = timesUp || allConfirmed;
  const [completeChecked, setCompleteChecked] = useState(false);

  const handleComplete = (): void => {
    setCompleteChecked(true);
    onTransition(elapsed);
  };

  return (
    <main
      aria-label="Prep checklist screen"
      data-handedness={handedness}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <span style={{ color: theme.text.primary, fontSize: '1rem', fontWeight: 600 }}>
          {recipeFile?.name ?? 'Prep in progress.'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ColourToggle value={colorScheme} onChange={setColorScheme} />
          <SpeakerToggle enabled={soundEnabled} onChange={setSoundEnabled} />
          <HandednessToggle value={handedness} onChange={setHandedness} />
          <TimerDisplay
            seconds={elapsed}
            totalSeconds={budgetSeconds ?? undefined}
            label={budgetApproximate ? 'Prep (≈ suggested)' : 'Prep'}
          />
        </div>
      </div>

      {/* Stage indicator — the "PrepCook" chip on a progress line toward the
          advisory budget (declared prepTime, else the 30-min default). The
          finish is the suggested end (prepStartedAt + budget); the header
          timer already labels it approximate. */}
      <Timeline
        mode="prep"
        phaseChip="PrepCook"
        progress={budgetSeconds && budgetSeconds > 0 ? elapsed / budgetSeconds : 0}
        nowLabel={formatHHMM(new Date(correctedNow(clockOffset)).toISOString())}
        finishLabel={formatHHMM(
          new Date(
            (prepStartedAt ?? correctedNow(clockOffset)) + (budgetSeconds ?? 0) * 1000,
          ).toISOString(),
        )}
      />

      <ol
        aria-label="Prep steps"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        {steps.map((step) => {
          const isChecked = confirmed.has(step.id);
          const hasHelp = step.help.length > 0;
          return (
            <li key={step.id} style={{ position: 'relative' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: handedness === 'right' ? 'row-reverse' : 'row',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '10px',
                  background: isChecked ? theme.accent.calm : theme.bg.raised,
                  minHeight: '64px',
                }}
              >
                <Checkbox
                  state={isChecked ? 'checked' : 'unchecked'}
                  label={`Confirm prep: ${step.text}`}
                  onChange={() => confirmPrepItem(step.id)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600 }}>
                    <AssistText
                      text={step.text}
                      matcher={assistEnabled ? (assistMatcher ?? undefined) : undefined}
                      onAssistWord={
                        assistEnabled ? (word) => handleAssistWord(step.id, word) : undefined
                      }
                    />
                  </span>
                  <StepMeta step={step} />
                </div>
                {hasHelp && (
                  <ChefHat ariaLabel={`Notes for ${step.text}`} onClick={() => openNote(step.id)} />
                )}
              </div>
              {openNoteId === step.id && hasHelp && (
                <NotePopover
                  title={step.text}
                  entries={step.help}
                  onClose={() => setOpenNoteId(null)}
                />
              )}
              {assist?.stepId === step.id && (
                <AssistPopover
                  word={assist.word}
                  entry={assist.entry}
                  onClose={() => setAssist(null)}
                />
              )}
            </li>
          );
        })}
      </ol>

      {showEndPanel && (
        <PhaseEndPanel
          phaseLabel="PrepCook"
          layout="horizontal"
          timesUp={timesUp}
          checked={completeChecked}
          canComplete={allConfirmed}
          checkboxSide={handedness}
          onToggle={handleComplete}
        />
      )}
    </main>
  );
}

export default PrepChecklistPane;
